import { useLang } from "@/lib/i18n";
import { useReveal } from "@/lib/useReveal";
import { whatsappUrl, CONTACT } from "@/lib/translations";

export const ContactMap = () => {
  const { lang, t } = useLang();
  const { ref, visible } = useReveal();

  return (
    <section
      id="contact"
      data-testid="contact-section"
      ref={ref}
      className={`py-20 md:py-32 bg-white border-t border-neutral-200 reveal-up ${visible ? "in-view" : ""}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-y-12 lg:gap-x-12">
          {/* Left column: content */}
          <div className="col-span-12 lg:col-span-5">
            <div className="overline mb-6">{t.contact.overline}</div>
            <h2 className="editorial-h text-5xl md:text-6xl lg:text-7xl mb-10 whitespace-pre-line">
              {t.contact.title}
            </h2>
            <p className="text-base md:text-[17px] leading-relaxed text-neutral-600 max-w-md mb-10">
              {t.contact.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a
                data-testid="contact-cta-whatsapp"
                href={whatsappUrl(lang)}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-black text-white hover:bg-neutral-800 transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Z" />
                </svg>
                <span className="text-[12px] tracking-[0.15em] uppercase">
                  {t.contact.ctaWhatsapp}
                </span>
              </a>
              <a
                data-testid="contact-cta-call"
                href={`mailto:${CONTACT.email}`}
                className="inline-flex items-center justify-center gap-3 px-7 py-4 border border-black hover:bg-black hover:text-white transition-colors text-[12px] tracking-[0.15em] uppercase"
              >
                {t.contact.ctaCall}
              </a>
            </div>

            <div className="space-y-6 border-t border-neutral-200 pt-8">
              <div>
                <div className="overline mb-2">{t.contact.whatsappLabel}</div>
                <a
                  data-testid="contact-whatsapp-number"
                  href={whatsappUrl(lang)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-serif text-2xl md:text-3xl tracking-tight hover:text-neutral-600 transition-colors"
                >
                  {CONTACT.whatsappDisplay}
                </a>
              </div>
              <div>
                <div className="overline mb-2">{t.contact.emailLabel}</div>
                <a
                  data-testid="contact-email"
                  href={`mailto:${CONTACT.email}`}
                  className="font-serif text-2xl md:text-3xl tracking-tight hover:text-neutral-600 transition-colors"
                >
                  {CONTACT.email}
                </a>
              </div>
            </div>
          </div>

          {/* Right column: map + address */}
          <div className="col-span-12 lg:col-span-7 lg:col-start-6">
            <div
              data-testid="map-wrapper"
              className="grayscale-map relative w-full aspect-[4/5] md:aspect-[5/4] lg:aspect-[4/5] bg-neutral-100 border border-neutral-200"
            >
              <iframe
                title="Girona Webs — Girona"
                src="https://www.google.com/maps?q=Girona,+Spain&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <div className="grid grid-cols-2 gap-6 mt-8">
              <div>
                <div className="overline mb-3">Estudio</div>
                <p
                  data-testid="contact-address"
                  className="font-serif text-xl md:text-2xl leading-tight tracking-tight whitespace-pre-line"
                >
                  {t.contact.address}
                </p>
              </div>
              <div>
                <div className="overline mb-3">Horario</div>
                <p className="font-serif text-xl md:text-2xl leading-tight tracking-tight">
                  {t.contact.hours}
                </p>
                <p className="text-sm text-neutral-500 mt-2">{t.contact.visit}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
